// Lógica pura para construir "threads" de trabajo (issue + PR + merge).
// Sin React, sin efectos — todo derivado de los arrays raw de GitHub API.
//
// Asociación issue↔PR↔merge:
// 1. Branch name del PR/merge: `<tipo>/<N>-...` → N = número del issue padre.
// 2. Body del PR: `Closes #N` / `Fixes #N` / `Resolves #N` (link GitHub oficial).
// 3. Branch tiene prioridad; body es fallback.
//
// Closed issues: cuando un merge resuelve un issue cerrado dentro de la
// ventana reciente, formamos un thread completo (issue cerrado + merge) en
// vez de "orphan". El thread queda visible hasta que el merge se reporte
// (label 'reported') — esto cubre el caso "ya está listo, falta tickear".

import type { Issue, PR, MergedPR } from './github'

const REPORTED_LABEL = 'reported'

export interface Thread {
  key: string
  issue?: Issue
  // Todos los PRs del thread (open + closed-sin-merge), ordenados por
  // created_at ascendente. Un issue puede tener varios intentos antes de
  // mergear; antes solo guardábamos el primero — la TV es pizarra de
  // historial, los necesitamos todos.
  prs: PR[]
  // Todos los merges del thread (un issue puede tener N PRs mergeados sobre
  // el mismo, ej. fix incremental). Ordenados por merged_at descendente — el
  // más reciente primero. La cell MERGE detallada muestra `merges[0]`; la
  // cell PRs renderiza una pill por cada uno.
  merges: MergedPR[]
}

export interface ThreadGroups {
  threads: Thread[]
  orphans: Thread[]
}

// Branch: acepta `<tipo>/<N>-<slug>` (estándar Wipxap) y `<tipo>/issue-<N>-<slug>`
// (variante observada en prod, ej `feature/issue-18-feriados`). El prefijo
// opcional `issue-` se tolera para asociar branches existentes sin force-push.
const BRANCH_PATTERN = /^(?:fix|feature|feat|refactor|chore|docs|hotfix)\/(?:issue-)?(\d+)(?:[-/]|$)/i

// Body: verbos GitHub oficiales (en) + equivalentes en español que el equipo
// usa al escribir PRs (`Cierra/Resuelve/Soluciona/Arregla #N`). GitHub NO
// autocierra issues con los verbos en español, pero WipHub sí los asocia para
// que el thread se vea correcto en la TV.
const BODY_PATTERN = /(?:closes?|fixes?|resolves?|cierra|resuelve|soluciona|arregla)\s+#(\d+)/gi

export function extractFromBranch(ref: string | undefined | null): number | null {
  if (!ref) return null
  const m = ref.match(BRANCH_PATTERN)
  return m ? Number(m[1]) : null
}

export function extractFromBody(body: string | null | undefined): number | null {
  if (!body) return null
  BODY_PATTERN.lastIndex = 0
  const m = BODY_PATTERN.exec(body)
  return m ? Number(m[1]) : null
}

function resolveIssueNumber(pr: { head?: { ref?: string }; body?: string | null }): number | null {
  return extractFromBranch(pr.head?.ref) ?? extractFromBody(pr.body)
}

const SEV_RANK: Record<string, number> = { p0: 0, p1: 1, p2: 2, p3: 3 }

function issueSeverityRank(issue: Issue): number {
  for (const l of issue.labels) {
    const name = l.name.toLowerCase()
    if (name in SEV_RANK) return SEV_RANK[name]
  }
  return 99
}

export function buildThreads(
  openIssues: Issue[],
  openPRs: PR[],
  closedPRs: PR[],
  merges: MergedPR[],
  closedIssues: Issue[] = [],
): ThreadGroups {
  // Index threads abiertos por issueNumber.
  const byIssue = new Map<number, Thread>()
  for (const i of openIssues) {
    byIssue.set(i.number, { key: `issue-${i.number}`, issue: i, prs: [], merges: [] })
  }

  // Closed issues recientes — indexados aparte. Sólo formamos thread a partir
  // de un closed issue si un PR o merge lo referencia explícitamente. Si no
  // hay PR/merge que lo referencie, NO lo mostramos (sería ruido: issue
  // cerrado limpio sin actividad pendiente de reportar).
  const closedByNumber = new Map<number, Issue>()
  for (const i of closedIssues) {
    if (!byIssue.has(i.number)) closedByNumber.set(i.number, i)
  }

  // Threads "huérfanos" — PR/merge sin issue resolvable ni en open ni en closed.
  const orphans: Thread[] = []

  // Procesamos open + closed PRs en un solo paso. La cell PRs los muestra a
  // todos como pills (open=azul, closed=rojo). Manten la presencia retroactiva.
  const allPRs: PR[] = [...openPRs, ...closedPRs]
  for (const pr of allPRs) {
    const n = resolveIssueNumber(pr)
    if (n == null) {
      orphans.push({ key: `orphan-pr-${pr.number}`, prs: [pr], merges: [] })
      continue
    }
    if (byIssue.has(n)) {
      byIssue.get(n)!.prs.push(pr)
    } else if (closedByNumber.has(n)) {
      // PR cuyo issue padre ya fue cerrado — forma thread con el closed issue.
      const closedIssue = closedByNumber.get(n)!
      byIssue.set(n, { key: `issue-${n}`, issue: closedIssue, prs: [pr], merges: [] })
    } else {
      orphans.push({ key: `orphan-pr-${pr.number}`, prs: [pr], merges: [] })
    }
  }

  for (const m of merges) {
    const n = resolveIssueNumber(m)
    if (n == null) {
      orphans.push({ key: `orphan-merge-${m.number}`, prs: [], merges: [m] })
      continue
    }
    if (byIssue.has(n)) {
      byIssue.get(n)!.merges.push(m)
    } else if (closedByNumber.has(n)) {
      // Merge cerró su issue padre. Antes íbamos a orphans → el thread
      // desaparecía. Ahora formamos thread completo (issue cerrado + merge);
      // el render lo muestra como "ready-to-report" hasta que se tickee.
      const closedIssue = closedByNumber.get(n)!
      byIssue.set(n, { key: `issue-${n}`, issue: closedIssue, prs: [], merges: [m] })
    } else {
      orphans.push({ key: `orphan-merge-${m.number}`, prs: [], merges: [m] })
    }
  }

  // PRs de cada thread ordenados por created_at ascendente (timeline visual:
  // el PR más viejo a la izquierda, el más nuevo a la derecha). Merges
  // ordenados por merged_at DESCENDENTE — el más reciente primero, ese es
  // el que la cell MERGE detallada muestra en pantalla.
  for (const t of byIssue.values()) {
    t.prs.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
    t.merges.sort((a, b) => mergeTimestamp(b) - mergeTimestamp(a))
  }

  const threads = Array.from(byIssue.values()).sort((a, b) => {
    // Ordenamiento por bucket (accionabilidad descendente):
    //   bucket 0: issues abiertos sin merge → arriba (lo accionable).
    //   bucket 1: con merge pero NO reportado → ready-to-report.
    //   bucket 2: con merge ya reportado → abajo (ya cerrado el loop).
    const ba = threadBucket(a)
    const bb = threadBucket(b)
    if (ba !== bb) return ba - bb

    // Dentro de buckets con merge (1 y 2): orden por merged_at del más reciente
    // descendente — lo más nuevo mandando desde arriba.
    if (ba !== 0 && a.merges[0] && b.merges[0]) {
      const ta = mergeTimestamp(a.merges[0])
      const tb = mergeTimestamp(b.merges[0])
      if (ta !== tb) return tb - ta
    }

    // Bucket 0 (issues abiertos) o desempate: severidad ascendente (P0 arriba),
    // luego número descendente (más reciente primero).
    const sa = a.issue ? issueSeverityRank(a.issue) : 99
    const sb = b.issue ? issueSeverityRank(b.issue) : 99
    if (sa !== sb) return sa - sb
    const na = a.issue?.number ?? a.prs[0]?.number ?? a.merges[0]?.number ?? 0
    const nb = b.issue?.number ?? b.prs[0]?.number ?? b.merges[0]?.number ?? 0
    return nb - na
  })

  orphans.sort((a, b) => {
    const na = a.prs[0]?.number ?? a.merges[0]?.number ?? 0
    const nb = b.prs[0]?.number ?? b.merges[0]?.number ?? 0
    return nb - na
  })

  return { threads, orphans }
}

// Thread es "merged" cuando tiene al menos un merge. El bucket se decide por
// si el merge principal (más reciente) está reportado o no.
function threadBucket(t: Thread): number {
  if (t.merges.length === 0) return 0
  const principal = t.merges[0]
  const reported = principal.labels.some((l) => l.name === REPORTED_LABEL)
  return reported ? 2 : 1
}

function mergeTimestamp(m: MergedPR): number {
  return m.merged_at ? Date.parse(m.merged_at) : 0
}
